import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Bell, Check, Loader2, ChevronRight } from "lucide-react";
import { api } from "../lib/api";
import EmptyState from "../components/EmptyState";
import { Skeleton } from "../components/Skeleton";
import { timeAgo } from "../lib/date";

const EMPTY_ILLUSTRATION =
  "https://static.prod-images.emergentagent.com/jobs/1708729f-9648-4829-9f8b-880cdb94ab73/images/dbc2ef0c9db61f1146fb8d05447a662edab3d51a4c2a41690bac30f7aab9358b.png";

const TYPE_LABEL = {
  new_post_in_scope: "Nouveau post",
  official_announcement: "Annonce officielle",
  post_approved: "Post approuvé",
  post_rejected: "Post refusé",
  moderation_pending: "Post à modérer",
  moderation_reminder: "Rappel modération",
  moderation_escalated: "Modération escaladée",
  new_comment: "Nouveau commentaire",
  new_reaction: "Nouvelle réaction",
  comment_approved: "Commentaire publié",
  event_attending_confirmation: "Inscription confirmée",
  event_reminder_7days: "Événement dans 7 jours",
  event_reminder_1day: "Événement demain",
  event_attendee_update: "Nouvelle inscription",
  post_reported: "Post signalé",
};

export default function Notifications() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () =>
    api
      .get("/notifications")
      .then((r) => {
        setItems(r.data.notifications);
        setLoading(false);
      })
      .catch(() => setLoading(false));

  useEffect(() => {
    load();
  }, []);

  const markAll = async () => {
    await api.patch("/notifications/read-all");
    load();
  };

  const markOne = async (id) => {
    await api.patch("/notifications/read", { notification_ids: [id] });
  };

  const handleClick = async (n) => {
    if (!n.is_read) {
      await markOne(n.notification_id);
    }
    // Navigation contextuelle
    const pid = n.payload?.post_id || n.payload?.related_post_id;
    if (pid) {
      navigate(`/posts/${pid}`);
    } else if (
      ["moderation_pending", "moderation_reminder", "moderation_escalated"].includes(
        n.notification_type
      )
    ) {
      navigate("/moderation");
    } else {
      load();
    }
  };

  const unreadCount = items.filter((n) => !n.is_read).length;

  return (
    <div data-testid="notifications-page">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <Bell size={28} color="#F77F00" />
          <div>
            <h1
              className="text-[28px] sm:text-[32px] font-bold leading-tight"
              style={{ fontFamily: "'Sora', sans-serif" }}
            >
              Notifications
            </h1>
            <p className="text-sm" style={{ color: "#666" }}>
              {unreadCount > 0 ? `${unreadCount} non lue${unreadCount > 1 ? "s" : ""}` : "Tout est à jour"}
            </p>
          </div>
        </div>
        {unreadCount > 0 && (
          <button
            onClick={markAll}
            data-testid="notif-mark-all-read"
            className="text-sm font-semibold transition-colors hover:underline"
            style={{ color: "#F77F00" }}
          >
            Tout marquer comme lu
          </button>
        )}
      </div>

      {loading && (
        <div className="space-y-2">
          {[0, 1, 2, 3].map((i) => (
            <div
              key={i}
              className="bg-white rounded-[12px] p-4"
              style={{ border: "1px solid #E5E5E5" }}
            >
              <Skeleton className="w-32 h-4 mb-2" />
              <Skeleton className="w-3/4 h-3" />
            </div>
          ))}
        </div>
      )}

      {!loading && items.length === 0 && (
        <EmptyState
          illustration={EMPTY_ILLUSTRATION}
          title="Aucune notification"
          description="Lorsque l'on vous mentionne ou que vos posts évoluent, vous serez prévenu ici."
          testid="notif-empty"
        />
      )}

      <AnimatePresence mode="popLayout">
        {items.map((n) => (
          <motion.button
            layout
            key={n.notification_id}
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            onClick={() => handleClick(n)}
            data-testid={`notif-${n.notification_id}`}
            className="w-full text-left bg-white rounded-[12px] p-4 mb-2 flex items-start gap-3 cursor-pointer transition-colors hover:bg-orange-50/40"
            style={{
              border: "1px solid #E5E5E5",
              borderLeft: n.is_read ? "1px solid #E5E5E5" : "3px solid #F77F00",
            }}
          >
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold mb-0.5" style={{ color: "#1A1A1A" }}>
                {TYPE_LABEL[n.notification_type] || n.notification_type}
              </div>
              <div className="text-xs line-clamp-2" style={{ color: "#666" }}>
                {n.payload?.title ||
                  n.payload?.message ||
                  n.payload?.author_name ||
                  ""}
              </div>
              <div className="text-xs mt-1" style={{ color: "#999" }}>
                {timeAgo(n.created_at)}
              </div>
            </div>
            {!n.is_read && (
              <span
                className="w-2 h-2 rounded-full flex-shrink-0 mt-2"
                style={{ background: "#F77F00" }}
              />
            )}
            <ChevronRight size={14} color="#999" className="mt-1 flex-shrink-0" />
          </motion.button>
        ))}
      </AnimatePresence>
    </div>
  );
}
